import PropTypes from 'prop-types';
/** @jsx jsx */
import React from 'react';
import styled from '@emotion/styled';
import { FormattedMessage, Link, injectIntl } from 'gatsby-plugin-react-intl';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Text, Themed, jsx } from 'theme-ui';
import { graphql } from 'gatsby';

import { cutDescriptionShort } from '../../utils/helpers';
import { spacing } from '../../utils/styles';

const TRANSITION_DURATION = '250ms';

const ProductListingItemLink = styled(Link)`
  margin-bottom: ${spacing.lg}px;
  overflow: hidden;
  text-decoration: none;
  transition: all ${TRANSITION_DURATION};
  width: 100%;
  max-width: 40rem;

  @media (hover: hover) {
    &:hover {
      transform: scale(1.03);
    }
  }
`;

const Item = styled(`article`)`
  display: flex;
  height: 100%;
  padding: ${spacing.lg}px;
  position: relative;
  flex-direction: column;
`;

const Preview = styled.div`
  margin-top: -3.5rem;
  margin-bottom: -2rem;
  max-height: 30rem;
  overflow: hidden;
  position: relative;
  z-index: 0;

  .gatsby-image-wrapper {
    transition: all ${TRANSITION_DURATION};
  }

  @media (hover: hover) {
    ${ProductListingItemLink}:hover & {
      .gatsby-image-wrapper {
        transform: scale(1.1);
      }
    }
  }
`;

const ProductListingItem = (props) => {
  const {
    intl,
    product: {
      categoryHandle,
      handle,
      images: [firstImage],
      story,
      title,
      variants: [firstVariant],
    },
  } = props;

  const { availableForSale, compareAtPrice, price, weight } = firstVariant;

  const image =
    (firstImage.localFile &&
      firstImage.localFile.childImageSharp &&
      firstImage.localFile.childImageSharp.gatsbyImageData) ||
    null;

  return (
    <ProductListingItemLink
      to={`/products/${categoryHandle}/${handle}/`}
      aria-label={title}
      sx={{ color: 'text' }}
    >
      <Item>
        <Preview>
          <GatsbyImage image={image} alt={title} />
          {!availableForSale && (
            <div
              sx={{
                position: 'absolute',
                top: '1.2rem',
                right: 0,
                bg: 'muted',
                color: 'mutedText',
                px: 2,
                py: 0,
                borderRadius: '5px',
              }}
            >
              <FormattedMessage id="productItem.soldOut" />
            </div>
          )}
        </Preview>
        <Themed.h3 sx={{ zIndex: 1 }}>{title}</Themed.h3>
        <Text
          sx={{
            flexGrow: 1,
            fontSize: [1, 1],
            mt: 2,
            mb: 3,
            textAlign: 'left',
          }}
        >
          {cutDescriptionShort(story, intl.locale === 'zh' ? 50 : 90)}
        </Text>
        <Text>
          <Text as="span">
            <FormattedMessage id="hkd" />
          </Text>{' '}
          <Text as="span" sx={{ color: 'primary' }}>
            {compareAtPrice && <s>${compareAtPrice}</s>} ${price}
          </Text>
          {weight > 0 && (
            <React.Fragment>
              {' '}
              / {weight}
              <FormattedMessage id="*.gram" />
            </React.Fragment>
          )}
        </Text>
      </Item>
    </ProductListingItemLink>
  );
};

ProductListingItem.propTypes = {
  intl: PropTypes.object,
  product: PropTypes.object.isRequired,
};

export const query = graphql`
  fragment ProductListingItem on ShopifyProduct {
    id
    handle
    variants {
      compareAtPrice
      shopifyId
      title
      price
      weight
      availableForSale
    }
  }
`;

export default injectIntl(ProductListingItem);
